import React, { useState, useRef, useEffect } from 'react';

const HelperButton = ({ 
  currentStep, 
  onNextStep, 
  themeMode, 
  currentTheme, 
  isVisible,
  selectedBranch,
  selectedUsecase,
  selectedSubUsecase,
  selectedQuestions,
  selectedDate,
  selectedTime,
  tableData,
  totalErrorCount,
  correctedErrorsCount,
  continueClicked,
  onDownloadTemplate,
  onAddManually,
  onValidate,
  onShowErrors,
  batchAlias = "", // New prop for batch alias
  // Additional props for customization
  suggestions = {},
  tips = {},
  totalSteps = 8,
  position = { top: '20px', right: '20px' }
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [buttonPosition, setButtonPosition] = useState(position);
  
  const buttonRef = useRef(null);
  const tooltipRef = useRef(null);

  // Default suggestions - can be overridden by props
  const defaultSuggestions = {
    initial: [
      "Welcome! Let's get started with call automation.",
      "First, select a branch from the dropdown menu.",
      "This will determine the type of calls you want to automate."
    ],
    branchSelected: [
      "Great! Now select a usecase and sub-usecase.",
      "Click the 'Select' button to choose your specific usecase.",
      "This defines the purpose and scope of your automated calls."
    ],
    usecaseSelected: [
      "Perfect! Now you can add custom questions if needed.",
      "Click 'Select' next to Custom Questions to add specific questions.",
      "Or skip this step and continue with the default questions."
    ],
    questionsSelected: [
      "Excellent! Now select a date for your calls.",
      "Choose the date when you want the automated calls to be scheduled.",
      "Make sure to pick a valid date."
    ],
    dateSelected: [
      "Great! Now select a time for your calls.",
      "Choose the time when you want the automated calls to be scheduled.",
      "Make sure to pick an available time slot."
    ],
    dateTimeSelected: [
      "Almost there! Now you can add patient data.",
      "Click 'Continue' to proceed to the data upload section.",
      "You can either add data manually or upload a file."
    ],
    readyToUpload: [
      "Perfect! Now you can add patient data.",
      "You can either add data manually or upload a file.",
      "Use the 'Add Manually' button or drag and drop files to upload."
    ],
    dataUploaded: [
      "Data uploaded successfully! Review the information in the table.",
      "Check for any errors highlighted in red cells.",
      "Use the 'Validate Data' button to check for issues.",
      "Fix any errors before scheduling your batch."
    ],
    readyToSchedule: [
      "All set! Your data looks good and error-free.",
      "Click 'Schedule Batch' to start the automated calls.",
      "You can track the progress and results from your dashboard."
    ]
  };

  // Default tips - can be overridden by props
  const defaultTips = {
    initial: "💡 Tip: Download a template first to see what fields are required for your selected branch and usecase.",
    branchSelected: "💡 Tip: Each usecase has specific requirements. Make sure to review the template before adding data.",
    usecaseSelected: "💡 Tip: Custom questions are optional but can help gather more specific information during calls.",
    questionsSelected: "💡 Tip: Don't forget to add a batch alias to easily identify this batch later.",
    dateSelected: "💡 Tip: Choose a time when your patients are most likely to answer the phone.",
    dateTimeSelected: "💡 Tip: You can upload Excel (.xlsx). Make sure your data matches the template format.",
    readyToUpload: "💡 Tip: You can upload Excel (.xlsx). Make sure your data matches the template format. Or use 'Add Manually' for individual entries.",
    dataUploaded: "💡 Tip: Red highlighted cells indicate errors that need to be fixed. Use the 'Show Errors' button to see all issues at once.",
    readyToSchedule: "💡 Tip: Once scheduled, you can track call progress and results in your dashboard. Make sure all required fields are filled correctly."
  };

  // Use provided suggestions/tips or defaults
  const finalSuggestions = { ...defaultSuggestions, ...suggestions };
  const finalTips = { ...defaultTips, ...tips };

  const getCurrentSuggestions = () => {
    // If we have data, we're in the data management phase (regardless of continueClicked)
    if (tableData && tableData.length > 0) {
      if (totalErrorCount > 0) return finalSuggestions.dataUploaded;
      return finalSuggestions.readyToSchedule;
    }
    
    // If continueClicked is true but no data, we're in the file upload phase
    if (continueClicked) {
      return finalSuggestions.readyToUpload;
    }
    
    // Initial setup phase
    if (!selectedBranch) return finalSuggestions.initial;
    if (!selectedUsecase) return finalSuggestions.branchSelected;
    if (!selectedQuestions || selectedQuestions.length === 0) return finalSuggestions.usecaseSelected;
    if (!selectedDate) return finalSuggestions.questionsSelected;
    if (!selectedTime) return finalSuggestions.dateSelected;
    return finalSuggestions.dateTimeSelected;
  };

  const getCurrentTip = () => {
    // If we have data, we're in the data management phase (regardless of continueClicked)
    if (tableData && tableData.length > 0) {
      if (totalErrorCount > 0) return finalTips.dataUploaded;
      return finalTips.readyToSchedule;
    }
    
    // If continueClicked is true but no data, we're in the file upload phase
    if (continueClicked) {
      return finalTips.readyToUpload;
    }
    
    // Initial setup phase
    if (!selectedBranch) return finalTips.initial;
    if (!selectedUsecase) return finalTips.branchSelected;
    if (!selectedQuestions || selectedQuestions.length === 0) return finalTips.usecaseSelected;
    if (!selectedDate) return finalTips.questionsSelected;
    if (!selectedTime) return finalTips.dateSelected;
    return finalTips.dateTimeSelected;
  };

  const getCurrentStep = () => {
    // If we have data, we're in the data management phase (regardless of continueClicked)
    if (tableData && tableData.length > 0) {
      if (totalErrorCount > 0) return 7;
      return 8;
    }
    
    // If continueClicked is true but no data, we're waiting for data upload
    if (continueClicked) {
      return 6;
    }
    
    // Initial setup phase
    if (!selectedBranch) return 1;
    if (!selectedUsecase) return 2;
    if (!selectedQuestions || selectedQuestions.length === 0) return 3;
    if (!selectedDate) return 4;
    if (!selectedTime) return 5;
    return 5;
  };

  const getProgressPercentage = () => {
    const currentStep = getCurrentStep();
    return (currentStep / totalSteps) * 100;
  };

  const currentSuggestions = getCurrentSuggestions();
  const currentTip = getCurrentTip();

  const handleNextSuggestion = () => {
    setCurrentSuggestion((prev) => 
      prev < currentSuggestions.length - 1 ? prev + 1 : 0
    );
  };

  const handlePreviousSuggestion = () => {
    setCurrentSuggestion((prev) => 
      prev > 0 ? prev - 1 : currentSuggestions.length - 1
    );
  };

  // Dragging functionality
  const handleDragStart = (e) => {
    setIsDragging(true);
    const rect = buttonRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    e.preventDefault();
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    // Keep button within viewport bounds
    const maxX = window.innerWidth - 36; // button width
    const maxY = window.innerHeight - 36; // button height
    
    setButtonPosition({
      left: Math.max(0, Math.min(newX, maxX)),
      top: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, dragOffset]);

  // Smart positioning logic for tooltip
  const getTooltipPosition = () => {
    if (!buttonRef.current) return { top: '50px', right: '0' };
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const tooltipWidth = 280; // tooltip width
    const tooltipHeight = 400; // approximate tooltip height
    const margin = 20; // margin from edges
    const buttonSize = 36; // button size
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate available space in each direction
    const spaceRight = viewportWidth - buttonRect.right - margin;
    const spaceLeft = buttonRect.left - margin;
    const spaceTop = buttonRect.top - margin;
    const spaceBottom = viewportHeight - buttonRect.bottom - margin;
    
    // Determine the best position based on available space and button location
    const positions = [
      { direction: 'right', space: spaceRight, style: { top: '0', left: `${buttonSize + 10}px` } },
      { direction: 'left', space: spaceLeft, style: { top: '0', right: `${buttonSize + 10}px` } },
      { direction: 'top', space: spaceTop, style: { bottom: `${buttonSize + 10}px`, left: '0' } },
      { direction: 'bottom', space: spaceBottom, style: { top: `${buttonSize + 10}px`, left: '0' } }
    ];
    
    // Check if button is near edges (within 150px of edges)
    const isNearBottom = buttonRect.bottom > viewportHeight - 150;
    const isNearTop = buttonRect.top < 150;
    const isNearRight = buttonRect.right > viewportWidth - 150;
    const isNearLeft = buttonRect.left < 150;
    
    // Special case: If button is in bottom-right corner, prioritize top-left
    if (isNearBottom && isNearRight) {
      // Check if we have enough space for top-left positioning
      if (spaceTop >= tooltipHeight && spaceLeft >= tooltipWidth) {
        return { 
          bottom: `${buttonSize + 10}px`, 
          right: `${buttonSize + 10}px` 
        };
      }
      // If not enough space for top-left, try top-right
      if (spaceTop >= tooltipHeight && spaceRight >= tooltipWidth) {
        return { 
          bottom: `${buttonSize + 10}px`, 
          left: `${buttonSize + 10}px` 
        };
      }
      // If not enough space for top, try left
      if (spaceLeft >= tooltipWidth) {
        return { 
          top: '0', 
          right: `${buttonSize + 10}px` 
        };
      }
    }
    
    // Special case: If button is in bottom-left corner, prioritize top-right
    if (isNearBottom && isNearLeft) {
      if (spaceTop >= tooltipHeight && spaceRight >= tooltipWidth) {
        return { 
          bottom: `${buttonSize + 10}px`, 
          left: `${buttonSize + 10}px` 
        };
      }
      if (spaceTop >= tooltipHeight && spaceLeft >= tooltipWidth) {
        return { 
          bottom: `${buttonSize + 10}px`, 
          right: `${buttonSize + 10}px` 
        };
      }
      if (spaceRight >= tooltipWidth) {
        return { 
          top: '0', 
          left: `${buttonSize + 10}px` 
        };
      }
    }
    
    // Special case: If button is in top-right corner, prioritize bottom-left
    if (isNearTop && isNearRight) {
      if (spaceBottom >= tooltipHeight && spaceLeft >= tooltipWidth) {
        return { 
          top: `${buttonSize + 10}px`, 
          right: `${buttonSize + 10}px` 
        };
      }
      if (spaceBottom >= tooltipHeight && spaceRight >= tooltipWidth) {
        return { 
          top: `${buttonSize + 10}px`, 
          left: `${buttonSize + 10}px` 
        };
      }
      if (spaceLeft >= tooltipWidth) {
        return { 
          bottom: '0', 
          right: `${buttonSize + 10}px` 
        };
      }
    }
    
    // Special case: If button is in top-left corner, prioritize bottom-right
    if (isNearTop && isNearLeft) {
      if (spaceBottom >= tooltipHeight && spaceRight >= tooltipWidth) {
        return { 
          top: `${buttonSize + 10}px`, 
          left: `${buttonSize + 10}px` 
        };
      }
      if (spaceBottom >= tooltipHeight && spaceLeft >= tooltipWidth) {
        return { 
          top: `${buttonSize + 10}px`, 
          right: `${buttonSize + 10}px` 
        };
      }
      if (spaceRight >= tooltipWidth) {
        return { 
          bottom: '0', 
          left: `${buttonSize + 10}px` 
        };
      }
    }
    
    // Prioritize positions based on button location (single edge cases)
    let prioritizedPositions = [...positions];
    
    if (isNearRight) {
      // If near right edge, prioritize left position
      prioritizedPositions = positions.filter(p => p.direction === 'left')
        .concat(positions.filter(p => p.direction !== 'left'));
    } else if (isNearLeft) {
      // If near left edge, prioritize right position
      prioritizedPositions = positions.filter(p => p.direction === 'right')
        .concat(positions.filter(p => p.direction !== 'right'));
    } else if (isNearBottom) {
      // If near bottom, prioritize top position
      prioritizedPositions = positions.filter(p => p.direction === 'top')
        .concat(positions.filter(p => p.direction !== 'top'));
    } else if (isNearTop) {
      // If near top, prioritize bottom position
      prioritizedPositions = positions.filter(p => p.direction === 'bottom')
        .concat(positions.filter(p => p.direction !== 'bottom'));
    }
    
    // Find the first position that has enough space
    for (const position of prioritizedPositions) {
      if (position.direction === 'right' || position.direction === 'left') {
        if (position.space >= tooltipWidth) {
          return position.style;
        }
      } else {
        if (position.space >= tooltipHeight) {
          return position.style;
        }
      }
    }
    
    // If no position has enough space, use the position with the most space
    // and adjust the tooltip size if needed
    const bestPosition = prioritizedPositions[0];
    
    if (bestPosition.direction === 'right' || bestPosition.direction === 'left') {
      // For horizontal positions, center vertically
      return {
        ...bestPosition.style,
        top: '50%',
        transform: 'translateY(-50%)'
      };
    } else {
      // For vertical positions, center horizontally
      return {
        ...bestPosition.style,
        left: '50%',
        transform: 'translateX(-50%)'
      };
    }
  };

  const tooltipPosition = getTooltipPosition();

  // Recalculate tooltip position when button moves or window resizes
  useEffect(() => {
    const handleResize = () => {
      // Force re-render to recalculate position
      setShowTooltip(prev => {
        if (prev) {
          setTimeout(() => setShowTooltip(true), 0);
        }
        return prev;
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [buttonPosition]);

  // Get dynamic tooltip dimensions based on available space
  const getTooltipDimensions = () => {
    if (!buttonRef.current) return { width: '280px', maxHeight: '400px' };
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 20;
    
    // Calculate available space
    const spaceRight = viewportWidth - buttonRect.right - margin;
    const spaceLeft = buttonRect.left - margin;
    const spaceTop = buttonRect.top - margin;
    const spaceBottom = viewportHeight - buttonRect.bottom - margin;
    
    // Find the best position and adjust dimensions accordingly
    const positions = [
      { direction: 'right', space: spaceRight },
      { direction: 'left', space: spaceLeft },
      { direction: 'bottom', space: spaceBottom },
      { direction: 'top', space: spaceTop }
    ];
    
    positions.sort((a, b) => b.space - a.space);
    const bestPosition = positions[0];
    
    if (bestPosition.direction === 'right' || bestPosition.direction === 'left') {
      // Horizontal positioning - adjust width
      const maxWidth = Math.min(280, bestPosition.space - 20);
      return { width: `${maxWidth}px`, maxHeight: '80vh' };
    } else {
      // Vertical positioning - adjust height
      const maxHeight = Math.min(400, bestPosition.space - 20);
      return { width: '280px', maxHeight: `${maxHeight}px` };
    }
  };

  const tooltipDimensions = getTooltipDimensions();

  if (!isVisible) return null;

  return (
    <div 
      ref={buttonRef}
      style={{ 
        position: 'fixed', 
        ...buttonPosition, 
        zIndex: 9999,
        cursor: isDragging ? 'grabbing' : 'grab',
        padding: '8px',
        margin: '-8px'
      }}
      onMouseDown={handleDragStart}
    >
      {/* Floating Help Button */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        {/* Tooltip */}
        {showTooltip && (
          <div
            ref={tooltipRef}
            style={{
              position: 'absolute',
              ...tooltipPosition,
              ...tooltipDimensions,
              background: currentTheme.callAutomationPage.mainBackground,
              border: `1px solid ${currentTheme.callAutomationPage.activeButtonColor}20`,
              borderRadius: '12px',
              padding: '28px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              color: currentTheme.homePage.textColor,
              fontSize: '12px',
              lineHeight: '1.4',
              zIndex: 1001,
              overflowY: 'auto',
              overflowX: 'hidden',
              wordWrap: 'break-word',
            }}
          >
            {/* Error Count Section - Show when there are errors */}
            {tableData && tableData.length > 0 && totalErrorCount > 0 && (
              <div style={{ 
                marginBottom: '12px', 
                padding: '10px 12px',
                background: themeMode === 'dark' ? '#2d1f1f' : '#fff5f5',
                border: `1px solid ${themeMode === 'dark' ? '#4a2a2a' : '#fed7d7'}`,
                borderRadius: '8px',
                fontSize: '11px'
              }}>
                <div style={{ 
                  fontWeight: '600', 
                  marginBottom: '6px', 
                  color: '#ff4e3a',
                  fontSize: '12px'
                }}>
                  ⚠️ Error Summary
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '4px'
                }}>
                  <span style={{ color: '#ff4e3a', fontWeight: '500' }}>
                    Total Errors:
                  </span>
                  <span style={{ 
                    color: '#ff4e3a', 
                    fontWeight: '600',
                    fontSize: '13px'
                  }}>
                    {totalErrorCount}
                  </span>
                </div>
                {correctedErrorsCount > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                  }}>
                    <span style={{ color: '#4caf50', fontWeight: '500' }}>
                      Corrected:
                    </span>
                    <span style={{ 
                      color: '#4caf50', 
                      fontWeight: '600',
                      fontSize: '13px'
                    }}>
                      {correctedErrorsCount}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Tip Section */}
            <div style={{ 
              marginBottom: '12px', 
              padding: '10px 12px',
              background: themeMode === 'dark' ? '#2a1f3a' : '#fff8e1',
              border: `1px solid ${themeMode === 'dark' ? '#4a3f5a' : '#ffecb3'}`,
              borderRadius: '8px',
              fontSize: '11px',
              color: themeMode === 'dark' ? '#e8d5ff' : '#8d6e63',
              fontWeight: '500'
            }}>
              {currentTip}
            </div>

            {/* Progress Section */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '6px'
              }}>
                <span style={{ fontSize: '10px', color: '#888', fontWeight: '500' }}>Progress</span>
                <span style={{ fontSize: '10px', fontWeight: '600', color: currentTheme.callAutomationPage.activeButtonColor }}>
                  Step {getCurrentStep()} of {totalSteps}
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '3px',
                background: themeMode === 'dark' ? '#3a3a3a' : '#e0e0e0',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${getProgressPercentage()}%`,
                  height: '100%',
                  background: currentTheme.callAutomationPage.activeButtonColor,
                  borderRadius: '2px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>

            {/* Current Status */}
            <div style={{ 
              marginBottom: '12px', 
              padding: '10px 12px',
              background: themeMode === 'dark' ? '#1a1a2e' : '#f5f5f5',
              borderRadius: '8px',
              border: `1px solid ${themeMode === 'dark' ? '#2a2a3e' : '#e0e0e0'}`
            }}>
              <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '11px', color: currentTheme.callAutomationPage.activeButtonColor }}>
                Current Status
              </div>
              <div style={{ fontSize: '11px', marginBottom: '10px', lineHeight: '1.3' }}>
                {currentSuggestions[currentSuggestion]}
              </div>
              
              {/* Status Indicators - Grid Layout */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '6px',
                fontSize: '10px'
              }}>
                {/* Branch */}
                <div style={{
                  padding: '6px 8px',
                  borderRadius: '6px',
                  border: `2px solid ${selectedBranch ? '#4caf50' : '#ff4e3a'}`,
                  background: selectedBranch ? '#4caf5020' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontWeight: '500'
                }}>
                  <span>Branch</span>
                  <span style={{ color: selectedBranch ? '#4caf50' : '#ff4e3a', fontWeight: '600' }}>
                    {selectedBranch ? '✓' : '✗'}
                  </span>
                </div>

                {/* Usecase */}
                <div style={{
                  padding: '6px 8px',
                  borderRadius: '6px',
                  border: `2px solid ${selectedUsecase ? '#4caf50' : '#ff4e3a'}`,
                  background: selectedUsecase ? '#4caf5020' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontWeight: '500'
                }}>
                  <span>Usecase</span>
                  <span style={{ color: selectedUsecase ? '#4caf50' : '#ff4e3a', fontWeight: '600' }}>
                    {selectedUsecase ? '✓' : '✗'}
                  </span>
                </div>

                {/* Date */}
                <div style={{
                  padding: '6px 8px',
                  borderRadius: '6px',
                  border: `2px solid ${selectedDate ? '#4caf50' : '#ff4e3a'}`,
                  background: selectedDate ? '#4caf5020' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontWeight: '500'
                }}>
                  <span>Date</span>
                  <span style={{ color: selectedDate ? '#4caf50' : '#ff4e3a', fontWeight: '600' }}>
                    {selectedDate ? '✓' : '✗'}
                  </span>
                </div>

                {/* Time */}
                <div style={{
                  padding: '6px 8px',
                  borderRadius: '6px',
                  border: `2px solid ${selectedTime ? '#4caf50' : '#ff4e3a'}`,
                  background: selectedTime ? '#4caf5020' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontWeight: '500'
                }}>
                  <span>Time</span>
                  <span style={{ color: selectedTime ? '#4caf50' : '#ff4e3a', fontWeight: '600' }}>
                    {selectedTime ? '✓' : '✗'}
                  </span>
                </div>

                {/* Batch Alias */}
                <div style={{
                  padding: '6px 8px',
                  borderRadius: '6px',
                  border: `2px solid ${batchAlias.trim() ? '#4caf50' : '#ff4e3a'}`,
                  background: batchAlias.trim() ? '#4caf5020' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontWeight: '500',
                  gridColumn: '1 / -1'
                }}>
                  <span>Batch Alias</span>
                  <span style={{ color: batchAlias.trim() ? '#4caf50' : '#ff4e3a', fontWeight: '600' }}>
                    {batchAlias.trim() ? '✓' : '✗'}
                  </span>
                </div>

                {/* Data */}
                <div style={{
                  padding: '6px 8px',
                  borderRadius: '6px',
                  border: `2px solid ${tableData && tableData.length > 0 ? '#4caf50' : '#ff4e3a'}`,
                  background: tableData && tableData.length > 0 ? '#4caf5020' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontWeight: '500',
                  gridColumn: '1 / -1'
                }}>
                  <span>Data</span>
                  <span style={{ color: tableData && tableData.length > 0 ? '#4caf50' : '#ff4e3a', fontWeight: '600' }}>
                    {tableData && tableData.length > 0 ? '✓' : '✗'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            {getCurrentStep() === 1 && onDownloadTemplate && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', color: '#888', marginBottom: '6px', fontWeight: '500' }}>
                  Quick Actions:
                </div>
                <button
                  onClick={onDownloadTemplate}
                  style={{
                    background: currentTheme.callAutomationPage.activeButtonColor,
                    color: currentTheme.callAutomationPage.submitColor,
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '5px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    width: '100%',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => { e.target.style.opacity = '0.9'; }}
                  onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
                  title="Download template to see required fields"
                >
                  📥 Download Template
                </button>
              </div>
            )}

            {getCurrentStep() === 6 && tableData && tableData.length === 0 && onAddManually && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', color: '#888', marginBottom: '6px', fontWeight: '500' }}>
                  Quick Actions:
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={onAddManually}
                    style={{
                      background: currentTheme.callAutomationPage.activeButtonColor,
                      color: currentTheme.callAutomationPage.submitColor,
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '5px',
                      fontSize: '10px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      flex: 1,
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                    onMouseEnter={(e) => { e.target.style.opacity = '0.9'; }}
                    onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
                    title="Add data manually"
                  >
                    ✏️ Add Manually
                  </button>
                </div>
              </div>
            )}

            {getCurrentStep() === 7 && totalErrorCount > 0 && onValidate && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', color: '#888', marginBottom: '6px', fontWeight: '500' }}>
                  Quick Actions:
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={onValidate}
                    style={{
                      background: currentTheme.callAutomationPage.activeButtonColor,
                      color: currentTheme.callAutomationPage.submitColor,
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '5px',
                      fontSize: '10px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      flex: 1,
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                    onMouseEnter={(e) => { e.target.style.opacity = '0.9'; }}
                    onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
                    title="Validate and fix errors"
                  >
                    🔧 Validate Data
                  </button>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
              <button
                onClick={handlePreviousSuggestion}
                style={{
                  background: 'none',
                  border: 'none',
                  color: currentTheme.callAutomationPage.activeButtonColor,
                  cursor: 'pointer',
                  fontSize: '10px',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = currentTheme.callAutomationPage.activeButtonColor + '15';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'none';
                }}
              >
                ← Previous
              </button>
              <span style={{ fontSize: '10px', color: '#888', fontWeight: '500' }}>
                {currentSuggestion + 1} / {currentSuggestions.length}
              </span>
              <button
                onClick={handleNextSuggestion}
                style={{
                  background: 'none',
                  border: 'none',
                  color: currentTheme.callAutomationPage.activeButtonColor,
                  cursor: 'pointer',
                  fontSize: '10px',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = currentTheme.callAutomationPage.activeButtonColor + '15';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'none';
                }}
              >
                Next →
              </button>
            </div>
            
            {/* Close Button */}
            <button
              onClick={() => setShowTooltip(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                fontSize: '18px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                zIndex: 1002
              }}
              onMouseEnter={(e) => {
                e.target.style.background = themeMode === 'dark' ? '#2a2a2a' : '#f0f0f0';
                e.target.style.color = '#666';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'none';
                e.target.style.color = '#888';
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Help Button */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowTooltip(!showTooltip);
            }}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: currentTheme.callAutomationPage.activeButtonColor,
              border: 'none',
              color: currentTheme.callAutomationPage.submitColor,
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(255, 78, 58, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              position: 'relative',
              fontWeight: '600',
              userSelect: 'none'
            }}
            onMouseEnter={(e) => {
              if (!isDragging) {
                e.target.style.transform = 'scale(1.1)';
                e.target.style.boxShadow = '0 6px 20px rgba(255, 78, 58, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 4px 16px rgba(255, 78, 58, 0.3)';
            }}
            title={`Get help with next steps (${getCurrentStep()}/${totalSteps} completed) - Drag to move`}
          >
            ?
          </button>
          
          {/* Progress Ring */}
          <div style={{
            position: 'absolute',
            top: '-3px',
            left: '-3px',
            width: '42px',
            height: '42px',
            borderRadius: '50%',
            background: `conic-gradient(${currentTheme.callAutomationPage.activeButtonColor} ${getProgressPercentage()}%, transparent ${getProgressPercentage()}%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: -1,
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: currentTheme.callAutomationPage.mainBackground,
            }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelperButton; 